import { z } from 'zod';
/**
 * Aktivitätsprotokoll. Muster aus Atleo, Aktionstypen neu.
 *
 * Zwei Dinge, die hier nicht verhandelbar sind:
 *
 * - **Admin-Zugriffe werden protokolliert** (`internal: true`), und interne Einträge werden aus
 *   Kunden- und Lieferanten-Feeds gefiltert (§5, Punkt 3).
 * - **Activity-Items tragen kein TTL.** Sie belegen, wer wann welchen Geschäftsvorfall
 *   ausgelöst hat (§4.4).
 */
export const ActivityAction = z.enum([
    // Anfrage / Ausschreibung
    'rfq.created',
    'rfq.submitted',
    'rfq.broadcast',
    'rfq.deadline_extended',
    'rfq.closed',
    /** Der Kunde hat die Angebotsphase selbst vorzeitig beendet (D23) — nicht der Sweeper. */
    'rfq.closed_early',
    'rfq.awarded',
    /**
     * Der Betreiber hat die Übergabe freigegeben (D24) — der Moment, in dem Kundenkontakt und
     * exakte Adresse an den Betrieb gehen und die Kontaktdaten des Betriebs an den Kunden.
     *
     * Eigener Typ und nicht ein zweites `rfq.awarded`: Zwischen beiden liegt die
     * Provisionsrechnung, und die Frage „wurde vor oder nach Zahlungseingang freigegeben?" ist
     * genau die, die man später stellt. `metadata` trägt deshalb den Rechnungsstand mit.
     */
    'rfq.handover_released',
    'rfq.cancelled',
    'rfq.rejected',
    'rfq.expired',
    // Angebote
    'offer.submitted',
    'offer.revised',
    'offer.withdrawn',
    'invite.sent',
    'invite.viewed',
    'invite.declined',
    /** Ein noch nicht registrierter Betrieb wurde per E-Mail eingeladen (D32). */
    'invite.external_sent',
    /** Ein Betrieb hat die E-Mail-Einladung angenommen — daraus wurde eine gewöhnliche Einladung. */
    'invite.external_claimed',
    // Lieferant
    'supplier.created',
    'supplier.updated',
    'supplier.verified',
    'supplier.suspended',
    'supplier.rejected',
    'supplier.user_invited',
    'supplier.user_removed',
    // Provision und Rechnung
    'commission.accrued',
    'commission.invoiced',
    /** Zahlungserinnerung oder Mahnung verschickt. Die Stufe steht in `metadata.dunningLevel`. */
    'commission.dunned',
    'commission.paid',
    'commission.voided',
    // Plattform-Admin — abrechnungsrelevante Eingriffe, immer `internal: true`
    'admin.supplier_created',
    /**
     * Ein Admin hat fremde Stammdaten geöffnet. §5 Punkt 3 verlangt, dass **Zugriffe**
     * protokolliert werden, nicht nur Änderungen — als eigener Aktionstyp und nicht als
     * getarnte Statusänderung, sonst behauptet das Protokoll Eingriffe, die es nie gab.
     */
    'admin.supplier_viewed',
    /**
     * Ein Admin hat eine fremde Anfrage geöffnet — mit Kundenkontakt und exakter Adresse.
     * Eigener Typ, statt `admin.supplier_viewed` mitzubenutzen: Ein Protokoll, das zwei
     * verschiedene Zugriffe unter einem Namen führt, beantwortet die Frage „wer hat die
     * Kundendaten gesehen" nicht mehr.
     */
    'admin.rfq_viewed',
    'admin.supplier_status_changed',
    /** Prüfergebnis zu Handelsregister, USt-IdNr. oder Hygienenachweis (Phase 3). */
    'admin.supplier_compliance_updated',
    'admin.supplier_invite_resent',
    'admin.commission_rate_overridden',
    'admin.commission_voided',
    'admin.payment_recorded',
    'admin.awarded_on_behalf',
    /**
     * Ein Administrator hat einem weiteren Konto Plattform-Administratorenrechte gegeben oder
     * genommen.
     *
     * Beide Einträge liegen unter der Partition `PLATFORM#ADMINS` — der Vorgang hängt an keinem
     * Geschäftsobjekt, sondern an der Plattform selbst. Damit steht „wer wurde wann von wem
     * berechtigt" in einer Abfrage statt verstreut über zufällige Nachbarpartitionen.
     */
    'admin.platform_admin_added',
    'admin.platform_admin_removed',
    // Kundenmails mit manueller Freigabe — `customer_mail_held` kommt vom System, die drei
    // anderen vom Admin. Alle `internal: true`.
    /** Eine Kundenmail wartet auf Freigabe, statt sofort rauszugehen. */
    'rfq.customer_mail_held',
    'admin.customer_mail_sent',
    'admin.customer_mail_discarded',
    /** Der Schalter zwischen manueller Freigabe und automatischem Versand wurde umgelegt. */
    'admin.customer_mail_mode_changed',
]);
/**
 * Wer gehandelt hat.
 *
 * `system` deckt Cron und Worker ab (Fristen-Sweeper, Broadcast) — ohne diesen Fall müsste
 * jeder Hintergrundlauf einen Pseudo-Nutzer erfinden.
 */
export const ActivityActorSchema = z.object({
    type: z.enum(['requester', 'supplier', 'admin', 'system']),
    /** Cognito-`sub`, `supplierId` oder `null` bei `system`. */
    id: z.string().nullable(),
    email: z.email().nullable(),
    name: z.string().max(200).nullable().default(null),
});
export const ActivityTargetSchema = z.object({
    id: z.string().min(1),
    type: z.enum(['rfq', 'offer', 'supplier', 'commission', 'invite', 'chat_session', 'user', 'config']),
    label: z.string().max(200).default(''),
});
export const ActivityItemSchema = z.object({
    id: z.string().min(1),
    timestamp: z.iso.datetime(),
    actor: ActivityActorSchema,
    action: ActivityAction,
    target: ActivityTargetSchema,
    /**
     * Interne Einträge erscheinen **nie** in Kunden- oder Lieferanten-Feeds. Default `false`:
     * Wer einen internen Vorgang protokolliert, muss das bewusst tun.
     */
    internal: z.boolean().default(false),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
